import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddCircleOutline as AddIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  InfoOutlined as InfoIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface Item {
  id: string;
  text: string;
}

interface EditableSortableListProps {
  label: string;
  values: string[];
  callback: (newValues: string[]) => void;
  color?: string;
  disabled?: boolean;
  helperText?: string;
  inputHelperText?: string;
  sx?: any;
}

interface SortableListItemProps {
  item: Item;
  index: number;
  disabled: boolean;
  isEditing: boolean;
  currentEditText: string;
  onStartEdit: (item: Item) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onSetEditText: (text: string) => void;
  onDeleteItem: (id: string) => void;
  editInputRef: React.RefObject<HTMLInputElement>;
  color?: string;
}

const SortableListItem: React.FC<SortableListItemProps> = ({
  item,
  index,
  disabled,
  isEditing,
  currentEditText,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onSetEditText,
  onDeleteItem,
  editInputRef,
  color = 'primary',
}) => {
  const { t } = useTranslation();
  const dragControls = useDragControls();

  return (
    <Reorder.Item
      value={item}
      dragListener={false}
      dragControls={dragControls}
      style={{ marginBottom: '8px' }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: disabled ? 'action.disabledBackground' : 'background.paper',
          opacity: disabled ? 0.7 : 1,
          cursor: disabled ? 'not-allowed' : 'default',
        }}
      >
        {!disabled && (
          <Tooltip title={t('text.labels.dragToReorder')}>
            <IconButton
              size="small"
              onPointerDown={(e) => {
                e.preventDefault();
                dragControls.start(e, { snapToCursor: true });
              }}
              sx={{ cursor: 'grab', touchAction: 'none' }}
              aria-label={t('text.labels.dragToReorder')}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Box
          sx={{
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            mr: 1,
            flexShrink: 0,
            fontSize: '0.8rem',
            fontWeight: 'bold',
            color: disabled ? 'text.disabled' : 'text.primary',
            backgroundColor: disabled ? 'action.disabledBackground' : color,
          }}
        >
          {String.fromCharCode(65 + index)}
        </Box>
        {isEditing && !disabled ? (
          <TextField
            inputRef={editInputRef}
            value={currentEditText}
            onChange={(e) => onSetEditText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onSaveEdit();
              } else if (e.key === 'Escape') {
                onCancelEdit();
              }
            }}
            onBlur={onSaveEdit}
            variant="outlined"
            size="small"
            fullWidth
            autoFocus
          />
        ) : (
          <Typography
            variant="body1"
            sx={{
              flexGrow: 1,
              cursor: !disabled && !isEditing ? 'pointer' : 'default',
              wordBreak: 'break-word',
            }}
            onClick={() => !disabled && !isEditing && onStartEdit(item)}
            title={item.text}
          >
            {item.text}
          </Typography>
        )}
        {!disabled &&
          (isEditing ? (
            <>
              <Tooltip title={t('text.labels.save')}>
                <IconButton size="small" onClick={onSaveEdit} color="primary">
                  <SaveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('text.labels.cancel')}>
                <IconButton size="small" onClick={onCancelEdit}>
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip title={t('text.labels.edit')}>
                <IconButton size="small" onClick={() => onStartEdit(item)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('text.labels.delete')}>
                <IconButton size="small" onClick={() => onDeleteItem(item.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ))}
      </Paper>
    </Reorder.Item>
  );
};

export function EditableSortableList({
  label,
  values,
  callback,
  color = 'primary',
  disabled = false,
  helperText = '',
  inputHelperText = '',
  sx = {},
}: EditableSortableListProps) {
  const { t } = useTranslation();
  const [items, setItems] = useState<Item[]>([]);
  const [newItemText, setNewItemText] = useState('');
  const [error, setError] = useState(false);
  const [errorTextKey, setErrorTextKey] = useState('');

  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setItems((prevItems) => {
      const newMappedItems = values.map((valueText) => {
        // Try to find an existing item with the same text to reuse its ID.
        // This assumes item text is unique enough for this purpose.
        // If texts are not unique, a more sophisticated ID management or key from parent is needed.
        const existingItem = prevItems.find((pi) => pi.text === valueText);
        if (existingItem) {
          // Return a new object with the existing ID but potentially updated text (though usually same)
          // This ensures that if other properties of `Item` could change, they are fresh,
          // while ID is preserved.
          return { ...existingItem, text: valueText };
        }
        // New item text, generate a new ID.
        return {
          id: `item-${valueText}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          text: valueText,
        };
      });

      // Optimization: If the newly mapped items are identical in ID and order to the previous items,
      // return the previous items array reference to prevent unnecessary re-renders.
      if (
        prevItems.length === newMappedItems.length &&
        prevItems.every((prevItem, index) => prevItem.id === newMappedItems[index].id)
      ) {
        return prevItems;
      }
      return newMappedItems;
    });
  }, [values]);

  const handleAddNewItem = () => {
    if (newItemText.trim() === '') {
      setError(true);
      setErrorTextKey('required');
      return;
    }
    if (items.some((item) => item.text === newItemText.trim())) {
      setError(true);
      setErrorTextKey('alreadyExists');
      return;
    }
    // Create a temporary new item to get its text for the callback
    const tempNewItemText = newItemText.trim();
    const currentTexts = items.map((item) => item.text);
    callback([...currentTexts, tempNewItemText]);
    setNewItemText('');
    setError(false);
    setErrorTextKey('');
  };

  const handleDeleteItem = (idToDelete: string) => {
    const updatedItems = items.filter((item) => item.id !== idToDelete);
    callback(updatedItems.map((item) => item.text));
  };

  const handleStartEdit = (item: Item) => {
    setEditingItemId(item.id);
    setEditText(item.text);
  };

  const handleSaveEdit = () => {
    if (editingItemId === null) return;
    const trimmedEditText = editText.trim();
    if (trimmedEditText === '') {
      alert(t('error.required')); // Consider a more integrated error display
      return;
    }
    if (items.some((item) => item.id !== editingItemId && item.text === trimmedEditText)) {
      alert(t('error.alreadyExists')); // Consider a more integrated error display
      return;
    }

    const updatedItems = items.map((item) =>
      item.id === editingItemId ? { ...item, text: trimmedEditText } : item
    );
    callback(updatedItems.map((item) => item.text));
    setEditingItemId(null);
    setEditText('');
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditText('');
  };

  const handleReorder = (newOrder: Item[]) => {
    // newOrder contains Item objects with stable IDs from the Reorder.Group
    callback(newOrder.map((item) => item.text));
  };

  useEffect(() => {
    if (editingItemId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingItemId]);

  return (
    <FormControl fullWidth sx={{ ...sx, mt: 2 }}>
      <Stack direction="column">
        <InputLabel
          shrink
          sx={{
            fontSize: '1.1rem !important',
            color: (theme) =>
              `${!disabled ? theme.palette.text.primary : theme.palette.text.disabled} !important`,
            ml: -1,
            ...(helperText && { display: 'flex', alignItems: 'center' }),
          }}
        >
          {label}
          {helperText && (
            <Tooltip title={helperText} placement="top">
              <InfoIcon sx={{ ml: 0.5, fontSize: '1rem', color: 'action.active' }} />
            </Tooltip>
          )}
        </InputLabel>

        {!disabled && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1, mt: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              value={newItemText}
              onChange={(e) => {
                setNewItemText(e.target.value);
                if (error) {
                  setError(false);
                  setErrorTextKey('');
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddNewItem();
                }
              }}
              disabled={disabled}
              error={error}
              helperText={error ? t(`error.${errorTextKey}`) : inputHelperText || ''}
              placeholder=""
              size="medium"
            />
            <Button
              variant="contained"
              onClick={handleAddNewItem}
              disabled={disabled || newItemText.trim() === ''}
              startIcon={<AddIcon />}
              sx={{
                flexShrink: 0,
                height: '100%',
                mt:
                  // eslint-disable-next-line no-nested-ternary
                  error && (inputHelperText || t('text.labels.pressEnterToAdd'))
                    ? '0px'
                    : error
                      ? '0px'
                      : '0px',
              }} // Simplified logic, TextField helperText handles spacing
            >
              {t('text.buttons.add')}
            </Button>
          </Stack>
        )}

        <Reorder.Group
          axis="y"
          values={items}
          onReorder={handleReorder}
          style={{ listStyle: 'none', padding: 0 }}
        >
          {items.map((item, index) => (
            <SortableListItem
              key={item.id} // Crucial: Use stable ID for React key
              item={item}
              index={index}
              disabled={disabled}
              isEditing={editingItemId === item.id}
              currentEditText={editText}
              onStartEdit={handleStartEdit}
              onSaveEdit={handleSaveEdit}
              onCancelEdit={handleCancelEdit}
              onSetEditText={setEditText}
              onDeleteItem={handleDeleteItem}
              editInputRef={editInputRef}
              color={color}
            />
          ))}
        </Reorder.Group>
        {/* items.length === 0 && !disabled && (
          <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ py: 2 }}>
            {t('text.labels.noItems')}
          </Typography>
        ) */}
      </Stack>
    </FormControl>
  );
}
